import { type PaginationState } from '@tanstack/react-table'
import clsx from 'clsx'
import { Minus, Plus, X } from 'lucide-react'
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react'

import NeedsAttentionContext from 'components/context/NeedsAttentionContext'
import {
  type FilterTypes,
  type NAVv2ArrayValueFilters,
  type NAVv2Filters,
  type NAVv2SingleValueFilters
} from 'components/hooks/useGetBrokersMissingPayments'
import AnimateHeight from 'components/reusable/AnimateHeight'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import OpacityTransition from 'components/reusable/OpacityTransition'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterGroup,
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { Badge, Button, IconButton } from 'slab'

import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from '../types'
import AfterDateFilter from './AfterDateFilter'
import AssigneesFilter from './AssigneesFilter'
import BeforeDateFilter from './BeforeDateFilter'
import BrokerageFilter from './BrokerageFilter'
import CarrierNameFilter from './CarrierNameFilter'
import CoverageStartDateAfterFilter from './CoverageStartDateAfterFilter'
import CoverageStartDateBeforeFilter from './CoverageStartDateBeforeFilter'
import EmployerFilter from './EmployerFilter'
import EmploymentStatusFilter from './EmploymentStatusFilter'
import EnrollmentCategoryFilter from './EnrollmentCategoryFilter'
import EnrollmentStateFilter from './EnrollmentStateFilter'
import EnrollmentTypeFilter from './EnrollmentTypeFilter'
import MemberFilter from './MemberFilter'
import NeedsAttentionQuickFilters from './NeedsAttentionQuickFilters'
import PlanYearFilter from './PlanYearFilter'
import ReasonFilter from './ReasonFilter'
import ReimbursementOnlyFilter from './ReimbursementOnlyFilter'
import StatusFilter from './StatusFilter'
import SubmissionMethodFilter from './SubmissionMethodFilter'
import TagFilter from './TagsFilter'
import VipEmployerFilter from './VipEmployerFilter'

type SingleSelectFilterComponents = {
  after_date: React.FC<SingleSelectedValueFilter>
  before_date: React.FC<SingleSelectedValueFilter>
  coverage_start_date_after: React.FC<SingleSelectedValueFilter>
  coverage_start_date_before: React.FC<SingleSelectedValueFilter>
  reimbursement_only: React.FC<SingleSelectedValueFilter>
  vip_employer: React.FC<SingleSelectedValueFilter>
}

type MultiSelectFilterComponents = {
  employer_ids: React.FC<MultipleSelectedValuesFilter>
  member_ids: React.FC<MultipleSelectedValuesFilter>
  enrollment_types: React.FC<MultipleSelectedValuesFilter>
  statuses: React.FC<MultipleSelectedValuesFilter>
  reasons: React.FC<MultipleSelectedValuesFilter>
  carrier_names: React.FC<MultipleSelectedValuesFilter>
  submission_methods: React.FC<MultipleSelectedValuesFilter>
  brokerage_ids: React.FC<MultipleSelectedValuesFilter>
  plan_years: React.FC<MultipleSelectedValuesFilter>
  tag_ids: React.FC<MultipleSelectedValuesFilter>
  broker_ids: React.FC<MultipleSelectedValuesFilter>
  enrollment_categories: React.FC<MultipleSelectedValuesFilter>
  enrollment_states: React.FC<MultipleSelectedValuesFilter>
  employment_statuses: React.FC<MultipleSelectedValuesFilter>
}

const singleSelectFilters: SingleSelectFilterComponents = {
  after_date: AfterDateFilter,
  before_date: BeforeDateFilter,
  coverage_start_date_after: CoverageStartDateAfterFilter,
  coverage_start_date_before: CoverageStartDateBeforeFilter,
  reimbursement_only: ReimbursementOnlyFilter,
  vip_employer: VipEmployerFilter,
}

const multiSelectFilters: MultiSelectFilterComponents = {
  employer_ids: EmployerFilter,
  member_ids: MemberFilter,
  enrollment_types: EnrollmentTypeFilter,
  statuses: StatusFilter,
  reasons: ReasonFilter,
  carrier_names: CarrierNameFilter,
  submission_methods: SubmissionMethodFilter,
  brokerage_ids: BrokerageFilter,
  plan_years: PlanYearFilter,
  tag_ids: TagFilter,
  broker_ids: AssigneesFilter,
  enrollment_categories: EnrollmentCategoryFilter,
  enrollment_states: EnrollmentStateFilter,
  employment_statuses: EmploymentStatusFilter,
}

type FilterGroupType = 'Date' | 'Associations' | 'Enrollment' | 'Triaging'

type NAVFilterConfig = {
  key: keyof NAVv2SingleValueFilters | keyof NAVv2ArrayValueFilters
  label: string
  group: FilterGroupType
  isMultiValue: boolean
  isV2Filter?: boolean
}

type NAVFilterConfigurations = {
  [key: string]: NAVFilterConfig
}

export const filterConfigurations: NAVFilterConfigurations = {
  after_date: {
    key: 'after_date',
    label: 'After Date',
    group: 'Date',
    isMultiValue: false,
  },
  before_date: {
    key: 'before_date',
    label: 'Before Date',
    group: 'Date',
    isMultiValue: false,
  },
  coverage_start_date_after: {
    key: 'coverage_start_date_after',
    label: 'Coverage Start After',
    group: 'Date',
    isMultiValue: false,
    isV2Filter: true,
  },
  coverage_start_date_before: {
    key: 'coverage_start_date_before',
    label: 'Coverage Start Before',
    group: 'Date',
    isMultiValue: false,
    isV2Filter: true,
  },
  reimbursement_only: {
    key: 'reimbursement_only',
    label: 'Reimbursement Only',
    group: 'Enrollment',
    isMultiValue: false,
    isV2Filter: true,
  },
  employer_ids: {
    key: 'employer_ids',
    label: 'Employer',
    group: 'Associations',
    isMultiValue: true,
  },
  member_ids: {
    key: 'member_ids',
    label: 'Member',
    group: 'Associations',
    isMultiValue: true,
  },
  enrollment_types: {
    key: 'enrollment_types',
    label: 'Enrollment Type',
    group: 'Enrollment',
    isMultiValue: true,
  },
  statuses: {
    key: 'statuses',
    label: 'Status',
    group: 'Triaging',
    isMultiValue: true,
  },
  reasons: {
    key: 'reasons',
    label: 'Reason',
    group: 'Triaging',
    isMultiValue: true,
  },
  carrier_names: {
    key: 'carrier_names',
    label: 'Carrier Name',
    group: 'Enrollment',
    isMultiValue: true,
  },
  submission_methods: {
    key: 'submission_methods',
    label: 'Submission Methods',
    group: 'Enrollment',
    isMultiValue: true,
    isV2Filter: true,
  },
  brokerage_ids: {
    key: 'brokerage_ids',
    label: 'Brokerage',
    group: 'Associations',
    isMultiValue: true,
    isV2Filter: true,
  },
  plan_years: {
    key: 'plan_years',
    label: 'Plan Year',
    group: 'Date',
    isMultiValue: true,
  },
  tag_ids: {
    key: 'tag_ids',
    label: 'Tag',
    group: 'Triaging',
    isMultiValue: true,
    isV2Filter: true,
  },
  broker_ids: {
    key: 'broker_ids',
    label: 'Assignee',
    group: 'Triaging',
    isMultiValue: true,
    isV2Filter: true,
  },
  enrollment_categories: {
    key: 'enrollment_categories',
    label: 'Enrollment Category',
    group: 'Enrollment',
    isMultiValue: true,
    isV2Filter: true,
  },
  enrollment_states: {
    key: 'enrollment_states',
    label: 'State',
    group: 'Enrollment',
    isMultiValue: true,
    isV2Filter: true,
  },
  employment_statuses: {
    key: 'employment_statuses',
    label: 'Employment Status',
    group: 'Enrollment',
    isMultiValue: true,
    isV2Filter: true,
  },
  vip_employer: {
    key: 'vip_employer',
    label: 'VIP Employer',
    group: 'Associations',
    isMultiValue: false,
    isV2Filter: true,
  },
}

const getFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Triaging':
      return 0
    case 'Enrollment':
      return 1
    case 'Date':
      return 2
    case 'Associations':
      return 3
    default:
      return 4
  }
}

function generateFilterGroups (
  filterConfigurations: NAVFilterConfig[],
  currentFilters: NAVv2Filters = {},
  filterType: FilterTypes,
  isNAVv2ToolingEnabled = false
): ComboboxFilterGroup<string>[] {
  const optionsGroupedByLabel = filterConfigurations.reduce<
    Record<FilterGroupType, ComboboxFilterOption<string>[]>
  >(
    (acc, filter) => {
      const { group, key, label, isMultiValue, isV2Filter } = filter

      // Exclusion filters only work for multi-value filters.
      if (filterType === 'exclude' && !isMultiValue) {
        return acc
      }

      // If v2 flag is not enabled, skip the filter.
      if (!isNAVv2ToolingEnabled && isV2Filter) {
        return acc
      }

      const existingGroup = acc[group as FilterGroupType] || []

      // Disable the filter if its already in use.
      const disabled = isMultiValue
        ? !!currentFilters[key as keyof NAVv2ArrayValueFilters]?.[filterType]
        : !!currentFilters[key]

      existingGroup.push({ value: key, label, disabled })
      acc[group as FilterGroupType] = existingGroup
      return acc
    },
    { Date: [], Associations: [], Enrollment: [], Triaging: [] }
  )

  return Object.entries(optionsGroupedByLabel)
    .map(([groupLabel, options]) => ({
      groupLabel,
      options,
    }))
    .sort(
      (a, b) =>
        getFiltersConfigurationSortValue(a) -
        getFiltersConfigurationSortValue(b)
    )
}

const defaultPinnedOptionsByFilterValue = {
  employer_ids: [],
  member_ids: [],
  enrollment_types: [],
  statuses: [],
  reasons: [],
  carrier_names: [],
  submission_methods: [],
  brokerage_ids: [],
  plan_years: [],
  enrollment_categories: [],
  enrollment_states: [],
  employment_statuses: [],
  tag_ids: [{ label: 'Untagged', value: 'untagged' }],
  broker_ids: [{ label: 'Unassigned', value: 'unassigned' }],
}

export type NeedsAttentionFiltersProps = {
  filters: NAVv2Filters
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
}

function useToggleSelection (
  pagination: PaginationState,
  setPagination: Dispatch<SetStateAction<PaginationState>>
) {
  return useCallback(
    (
      value: string,
      key: keyof NAVv2ArrayValueFilters,
      setSelectedItems: React.Dispatch<
        React.SetStateAction<NAVv2ArrayValueFilters>
      >,
      type: FilterTypes
    ) => {
      setSelectedItems((prev) => {
        const existing = prev[key]?.[type] ?? []
        const otherType = type === 'exclude' ? 'include' : 'exclude'
        const other = prev[key]?.[otherType]

        // If the item is already in the list, remove it from the list. Otherwise, add it! This allows users to toggle.
        const updated = existing.includes(value)
          ? existing.filter((item) => item !== value)
          : [...existing, value]

        const otherFiltered = other?.filter((item) => !updated?.includes(item))

        return {
          ...prev,
          [key]: {
            [type]: updated,
            ...(otherFiltered ? { [otherType]: otherFiltered } : {}),
          },
        }
      })

      if (pagination.pageIndex !== 1) {
        setPagination({ ...pagination, pageIndex: 1 })
      }
    },
    [pagination, setPagination]
  )
}

type FilterProps = {
  type: 'include' | 'exclude'
  config: NAVFilterConfig
  children: React.ReactNode
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
}

const Filter = ({ type, config, children, setFilters }: FilterProps) => {
  const { label, isMultiValue, key } = config

  const handleRemoveFilter = () => {
    setFilters((prev) => {
      const newFilters = { ...prev }
      const multiValueKey = key as keyof NAVv2ArrayValueFilters

      if (isMultiValue) {
        if (newFilters[multiValueKey]?.[type]) {
          const updatedSubFilter = { ...newFilters[multiValueKey] }
          delete updatedSubFilter[type]

          if (Object.keys(updatedSubFilter).length === 0) {
            delete newFilters[config.key]
          } else {
            newFilters[multiValueKey] = updatedSubFilter
          }
        }
      } else {
        const singleValueKey = key as keyof NAVv2SingleValueFilters
        delete newFilters[singleValueKey]
      }

      return newFilters
    })
  }

  return (
    <div
      className={clsx({
        'p-1 my-1 rounded-md max-w-[600px] flex items-center justify-between gap-2 border shadow-sm':
          true,
      })}
    >
      <div className='font-semibold text-sm min-w-[150px] ml-2'>{label}</div>
      <div className='min-w-[70px]'>
        <IncludeOrExcludeBadge type={type} />
      </div>
      {children}
      <div className='flex-grow-1 flex-shrink-0'>
        <IconButton
          onClick={handleRemoveFilter}
          variant='destructive'
          label='Remove filter'
          icon={X}
        />
      </div>
    </div>
  )
}

const IncludeOrExcludeBadge = ({ type }: { type: 'include' | 'exclude' }) => (
  <Badge type={type === 'include' ? 'info' : 'critical'}>
    {type === 'include' ? 'Filter' : 'Exclude'}
  </Badge>
)

const NeedsAttentionFilters = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: NeedsAttentionFiltersProps) => {
  const [selectedFilterType, setSelectedFilterType] = useState<
    FilterTypes | undefined
  >()
  const [query, setQuery] = useState('')
  const { isNAVv2ToolingEnabled } = useContext(NeedsAttentionContext)

  const filteredConfigurationOptions = Object.values(
    filterConfigurations
  ).filter(
    ({ key, label }) =>
      label?.toLowerCase().includes(query.toLowerCase()) ||
      key.toLowerCase().includes(query.toLowerCase())
  )

  const toggleSelection = useToggleSelection(pagination, setPagination)

  const onSelectFilterType = (value: string) => {
    if (!selectedFilterType) return

    const filterType = value as keyof NAVv2Filters

    setFilters((prev) => {
      const filterConfig = filterConfigurations[filterType]
      const { key, isMultiValue } = filterConfig

      if (isMultiValue) {
        return {
          ...prev,
          [key]: {
            [selectedFilterType]: [],
            ...prev[key as keyof NAVv2ArrayValueFilters],
          },
        }
      } else {
        return {
          ...prev,
          [key]: '',
        }
      }
    })
    setSelectedFilterType(undefined)
  }

  return (
    <div className='space-y-2'>
      <NeedsAttentionQuickFilters filters={filters} setFilters={setFilters} />

      <AnimateHeight>
        <div className='flex flex-wrap items-center justify-start gap-1 flex-shrink-0'>
          {Object.entries(filters)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([filter, values]) => {
              const config =
                filterConfigurations[filter as keyof NAVFilterConfigurations]
              const { isMultiValue, isV2Filter } = config

              if (isV2Filter && !isNAVv2ToolingEnabled) {
                return null
              }

              if (isMultiValue) {
                return Object.entries(values)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([type, selectedValues]) => {
                    const FilterComponent =
                      multiSelectFilters[
                        filter as keyof MultiSelectFilterComponents
                      ]
                    const onSelectValue = (value: string) => {
                      toggleSelection(
                        value,
                        filter as keyof NAVv2ArrayValueFilters,
                        setFilters as Dispatch<
                          SetStateAction<NAVv2ArrayValueFilters>
                        >,
                        type as FilterTypes
                      )
                    }
                    const defaultPinnedOptions =
                      defaultPinnedOptionsByFilterValue[
                        filter as keyof typeof defaultPinnedOptionsByFilterValue
                      ]

                    const filterProps = {
                      type,
                      filterKey: filter,
                      selectedValues,
                      isMultiValue,
                      isV2Filter,
                      onSelectValue,
                      defaultPinnedOptions,
                    }

                    return (
                      <AnimateOpacity key={`${filter}-${type}`} isOpen>
                        <Filter
                          key={`${filter}-${type}`}
                          type={type as FilterTypes}
                          config={config}
                          setFilters={setFilters}
                        >
                          <div className='bg-white'>
                            {FilterComponent && (
                              <FilterComponent {...filterProps} />
                            )}
                          </div>
                        </Filter>
                      </AnimateOpacity>
                    )
                  })
              } else {
                const FilterComponent =
                  singleSelectFilters[
                    filter as keyof SingleSelectFilterComponents
                  ]
                const selectValue = (value: string) => {
                  setFilters((prev) => ({
                    ...prev,
                    [filter]: value,
                  }))
                  if (pagination.pageIndex !== 1) {
                    setPagination({ ...pagination, pageIndex: 1 })
                  }
                }

                const filterProps = {
                  filterKey: filter,
                  selectedValue: values as string,
                  isMultiValue,
                  isV2Filter,
                  selectValue,
                }

                return (
                  <AnimateOpacity key={filter} isOpen>
                    <Filter
                      key={filter}
                      type='include'
                      config={config}
                      setFilters={setFilters}
                    >
                      <div className='bg-white'>
                        {FilterComponent && (
                          <FilterComponent {...filterProps} />
                        )}
                      </div>
                    </Filter>
                  </AnimateOpacity>
                )
              }
            })}
        </div>
      </AnimateHeight>

      <div className='ml-1 pb-4 h-8'>
        {!selectedFilterType && (
          <OpacityTransition key='add-filter'>
            <div className='font-semibold text-sm flex items-center justify-start space-x-2'>
              <div>Add filter:</div>
              <Button
                variant='tertiary'
                onClick={() => setSelectedFilterType('include')}
              >
                <div className='flex items-center justify-center space-x-2'>
                  <div>Filter</div>
                  <div>
                    <Plus size={16} />
                  </div>
                </div>
              </Button>
              {isNAVv2ToolingEnabled && (
                <Button
                  variant='tertiary-destructive'
                  onClick={() => setSelectedFilterType('exclude')}
                >
                  <div className='flex items-center justify-center space-x-2'>
                    <div>Exclude</div>
                    <div>
                      <Minus size={16} />
                    </div>
                  </div>
                </Button>
              )}
            </div>
          </OpacityTransition>
        )}

        {selectedFilterType && (
          <OpacityTransition key={selectedFilterType}>
            <div className='flex items-center justify-start space-x-2 text-sm font-semibold'>
              <IncludeOrExcludeBadge type={selectedFilterType} />
              <ComboboxMultiSelectFilter
                placeholder='Search filter types...'
                options={generateFilterGroups(
                  filteredConfigurationOptions,
                  filters,
                  selectedFilterType,
                  isNAVv2ToolingEnabled
                )}
                selectedValues={[]}
                onSelectValue={onSelectFilterType}
                onValueChange={setQuery}
                isOpen
              >
                <Button variant='tertiary'>Select a filter type...</Button>
              </ComboboxMultiSelectFilter>
              <IconButton
                onClick={() => setSelectedFilterType(undefined)}
                variant='destructive'
                label='Remove filter'
                icon={X}
              />
            </div>
          </OpacityTransition>
        )}
      </div>
    </div>
  )
}

export default NeedsAttentionFilters
